import {
  GameEngine,
  EngineError,
  redactEventsForSeat,
  stepAI,
  captureEventBatch,
  ActionType,
  isNonPlayAction,
  EnginePhase,
  StandbyHoldTimer,
  buildCuratedAiDeck,
  parseAiDeckDifficulty,
  parseResponseMode,
  asCardFoil,
  parseAvatarConfig,
  pickCpuAvatar,
  type AvatarConfig,
  type CardData,
  type GameEvent,
  type MatchState,
  REPLAY_ENGINE_VERSION,
  replayDeckFromCards,
  type DuelReplay,
  type PlayerAction,
  type PlayerInfo,
  type ReplayDeck,
  type Seat,
} from '@shared/index'
import { ensurePlayableDeckList, resolveLocalCards } from '../localDeck'
import { useDuelPrefsStore } from '../../stores/duelPrefs'
import { usePlayerStore } from '../../stores/player'
import type { GameClient, GameClientHandlers } from './GameClient'
import lobData from '../../assets/lob-cards.json'

// Runs the SAME shared engine locally for the vs-AI mode. This validates that the
// rules engine is fully decoupled from Phaser (Fase 2b): the renderer consumes the
// identical GameEvents/state it would receive from the authoritative server.

function toCardData(raw: any): CardData {
  const card: CardData = {
    id: String(raw.id),
    name: raw.name,
    type: raw.type,
  }
  if (raw.level !== undefined) card.level = raw.level
  if (raw.typeline !== undefined) card.typeline = raw.typeline
  if (raw.humanReadableCardType !== undefined) card.humanReadableCardType = raw.humanReadableCardType
  if (raw.desc !== undefined) card.desc = raw.desc
  if (raw.atk !== undefined) card.atk = raw.atk
  if (raw.def !== undefined) card.def = raw.def
  if (raw.race !== undefined) card.race = raw.race
  if (raw.attribute !== undefined) card.attribute = raw.attribute
  if (raw.archetype !== undefined) card.archetype = raw.archetype
  if (raw.foil !== undefined) card.foil = asCardFoil(raw.foil)
  return card
}

const LOB_BY_ID = new Map(
  (lobData as { data: Record<string, unknown>[] }).data.map((raw) => [
    String(raw.id),
    toCardData(raw),
  ]),
)

export class LocalEngineClient implements GameClient {
  readonly seat: Seat = 0
  lastAction: PlayerAction | null = null
  private readonly handlerSets: GameClientHandlers[] = []
  private readonly engine: GameEngine
  private readonly replaySeed: number
  private readonly replayFirst: Seat
  private readonly replayNames: [string, string]
  private readonly replayDecks: [ReplayDeck, ReplayDeck]
  private readonly standbyHold: StandbyHoldTimer
  private overSent = false
  private disposed = false
  /** Bumped to drop an AI plan that is still queued on the event loop. */
  private aiGeneration = 0

  constructor(opts?: {
    firstPlayer?: Seat
    cpuAvatar?: AvatarConfig
  }) {
    const playerDeck = resolveLocalCards(ensurePlayableDeckList())
    if (!playerDeck) {
      throw new Error('A saved legal owned deck is required to start a local duel')
    }
    const catalog = [...LOB_BY_ID.values()]
    const difficulty = parseAiDeckDifficulty(
      useDuelPrefsStore().vsAiDifficulty,
    )
    const aiDeck = buildCuratedAiDeck(catalog, difficulty)
    const you = usePlayerStore()
    const seed = (Math.floor(Math.random() * 0xffffffff) >>> 0) || 1
    const firstPlayer: Seat = opts?.firstPlayer === 1 ? 1 : 0
    this.replaySeed = seed
    this.replayFirst = firstPlayer
    this.replayNames = [you.username || 'Player', 'CPU']
    this.replayDecks = [
      replayDeckFromCards(playerDeck.mainDeck.map(toCardData), playerDeck.extraDeck.map(toCardData)),
      replayDeckFromCards(aiDeck.mainDeck, aiDeck.extraDeck),
    ]
    const seats: [PlayerInfo, PlayerInfo] = [
      {
        id: 'you',
        username: you.username,
        isAI: false,
        connected: true,
        avatar: parseAvatarConfig(you.avatar),
      },
      {
        id: 'cpu',
        username: 'CPU',
        isAI: true,
        connected: true,
        avatar: parseAvatarConfig(
          opts?.cpuAvatar ?? pickCpuAvatar((Math.random() * 0xffffffff) >>> 0),
        ),
        aiDifficulty: difficulty,
      },
    ]
    this.engine = GameEngine.createMatch({
      id: 'local',
      seats,
      decks: [
        playerDeck.mainDeck.map(toCardData),
        aiDeck.mainDeck,
      ],
      extraDecks: [playerDeck.extraDeck.map(toCardData), aiDeck.extraDeck],
      catalog,
      seed,
      firstPlayer,
    })
    this.standbyHold = new StandbyHoldTimer(this.engine, (seat) => {
      this.applyStandbyAdvance(seat)
    })
  }

  setHandlers(handlers: GameClientHandlers): void {
    this.handlerSets.push(handlers)
  }

  dropHandlers(): void {
    this.handlerSets.length = 0
  }

  private fire<K extends keyof GameClientHandlers>(
    key: K,
    ...args: Parameters<NonNullable<GameClientHandlers[K]>>
  ): void {
    for (const set of this.handlerSets) {
      const fn = set[key] as ((...a: typeof args) => void) | undefined
      fn?.(...args)
    }
  }

  start(): void {
    const mode = parseResponseMode(useDuelPrefsStore().responseMode)
    this.engine.applyAction(this.seat, { type: ActionType.SET_RESPONSE_MODE, mode })
    this.fire('onStart', { state: this.engine.getStateForSeat(this.seat), viewerSeat: this.seat })
    if (this.engine.getState().status === 'finished') {
      this.emitOverIfNeeded()
      return
    }
    this.resolveOpeningDraw()
    this.scheduleAISteps()
    this.standbyHold.arm()
  }

  send(action: PlayerAction): void {
    this.standbyHold.clear()
    const play = !isNonPlayAction(action.type)
    // A prompt-mode change or hand shuffle is not a play; do not cancel the
    // AI plan or let it consume the opening Draw Phase as a side effect.
    // A rejected play must not bump the generation either. The accepted click
    // already queued the AI, and the duplicate (a second tap on the only
    // Flip target) used to invalidate that queue when the engine answered
    // "no flip pending". The Battle Phase then sat there with nobody to move.
    this.lastAction = action
    let applied = false
    try {
      const events = this.engine.applyAction(this.seat, action)
      applied = true
      if (play) this.aiGeneration += 1
      this.emitEvents(events)
    } catch (err) {
      if (err instanceof EngineError) this.fire('onError', err.message, err.code, err.vars)
      else throw err
    } finally {
      if (applied && play) this.scheduleAISteps()
      this.standbyHold.arm()
    }
  }

  /** Log for the account history. `intact` is false when the action list was dropped. */
  exportReplay(): DuelReplay & { intact: boolean } {
    const snap = this.engine.getState()
    return {
      version: REPLAY_ENGINE_VERSION,
      mode: 'ai',
      seed: this.replaySeed,
      firstPlayer: this.replayFirst,
      names: this.replayNames,
      decks: this.replayDecks,
      actions: this.engine.takeReplayActions(),
      winnerSeat: snap.winner,
      reason: snap.winReason,
      turnCount: snap.absoluteTurnCount,
      intact: this.engine.replayIsIntact(),
    }
  }

  requestState(): void {
    this.fire('onState', { state: this.engine.getStateForSeat(this.seat), viewerSeat: this.seat })
  }

  dispose(): void {
    this.disposed = true
    this.aiGeneration += 1
    this.standbyHold.clear()
    this.handlerSets.length = 0
  }

  /**
   * First turn starts on Draw Phase. Advance once (no extra card — the sixth
   * is already in hand) so the duel lands on Standby after the opening deal.
   */
  private resolveOpeningDraw(): void {
    const s = this.engine.getState()
    if (s.phase !== EnginePhase.DRAW) return
    this.emitEvents(
      this.engine.applyAction(s.turnPlayer, { type: ActionType.ADVANCE_PHASE }),
    )
  }

  /**
   * Host-side Standby skip: apply as the turn player (human or AI) so both
   * sides get the same 2s pause without a Vue timer racing ADVANCE_PHASE.
   */
  private applyStandbyAdvance(seat: Seat): void {
    this.aiGeneration += 1
    try {
      this.emitEvents(this.engine.applyAction(seat, { type: ActionType.ADVANCE_PHASE }))
      this.scheduleAISteps()
    } catch (err) {
      this.lastAction = null
      if (err instanceof EngineError) this.fire('onError', err.message, err.code, err.vars)
      else throw err
    } finally {
      this.standbyHold.arm()
    }
  }

  /**
   * One AI action per turn of the event loop, so the turn-change draw can
   * paint before the next search. Planning the whole turn inside the click
   * froze the phone.
   */
  private scheduleAISteps(): void {
    const gen = this.aiGeneration
    let stepped = false
    const pump = (): void => {
      if (this.disposed || gen !== this.aiGeneration) return
      let events: GameEvent[] | null = null
      try {
        events = stepAI(this.engine)
      } catch (err) {
        this.lastAction = null
        if (err instanceof EngineError) this.fire('onError', err.message, err.code, err.vars)
        else throw err
        this.standbyHold.arm()
        return
      }
      if (!events || events.length === 0) {
        if (stepped) this.standbyHold.arm()
        return
      }
      const batch = captureEventBatch(this.engine, events)
      if (!batch) {
        if (stepped) this.standbyHold.arm()
        return
      }
      stepped = true
      this.emitEvents(batch.events, batch.states[this.seat])
      setTimeout(pump, 0)
    }
    setTimeout(pump, 0)
  }

  private emitEvents(events: GameEvent[], state?: MatchState): void {
    if (events.length === 0) return
    const filtered = redactEventsForSeat(events, this.seat)
    this.fire('onEvents', {
      events: filtered,
      // Prefer the batch snapshot. The live engine may already be on the
      // next action, and reading it here would skip this phase.
      state: state ?? this.engine.getStateForSeat(this.seat),
      viewerSeat: this.seat,
    })
    this.emitOverIfNeeded()
  }

  private emitOverIfNeeded(): void {
    if (this.engine.getState().status !== 'finished' || this.overSent) return
    this.overSent = true
    const snap = this.engine.getState()
    this.fire('onOver', {
      winner: snap.winner,
      winReason: snap.winReason,
      metrics: this.engine.getDuelStats(this.seat),
    })
  }
}
